import React, { useEffect, useState } from "react";

import { DisketteSaveIcon, MuscleIcon, GlutenFreeBlackIcon, VeganWhiteIcon } from "../../../assets/icons";
import ProductCardType1 from "../../shared/products cards/ProductCardType1";
import ProductCardType2 from "../../shared/products cards/ProductCardType2";
import { CategoryStyle, ProductCardStyle } from "../../../models/base/Theme";
import { ThemeInfoDTO } from "../../../models/theme/theme.response";
import { ProductCardStyleType } from "../../../models/base/ProductCardStyleType";
import { CategoryStyleType } from "../../../models/base/CategoryStyleType";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useParams } from "react-router-dom";
import { themeService } from "../../../services/themeService";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import Loader from "../../shared/Loader";
import TagTypes from "../../shared/products cards/TagTypes";
import { TagStyleType } from "../../../models/base/TagStyleType";
import { ProductTagType } from "../../../models/base/ProductTagType";
import CategoryTypes from "../../client/main-page/CategoryTypes";
import ProductCardType3 from "../../shared/products cards/ProductCardType3";
import ProductCardType4 from "../../shared/products cards/ProductCardType4";
import { Product } from "../../shared/products cards/types";
import { ProductVariantDTO } from "../../../models/local/local.response";
import { useCart } from "../../../hooks/useCart";

const ThemePage: React.FC = () => {

    const [theme, setTheme] = useState<ThemeInfoDTO | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();
    const cart = useCart();

    // Mock data za preview
    const mockProduct: Product = {
        id: "1",
        name: "Pečena piletina",
        description: "Sočna pečena piletina sa začinskim biljem",
        description2: "Poslužena sa prilogom po izboru",
        imageUrl: "https://assets.epicurious.com/photos/5b843bce1abfc56568396369/1:1/w_2560%2Cc_limit/Grilled-Chicken-with-Mustard-Sauce-and-Tomato-Salad-recipe-2-22082018.jpg",
        isNew: true,
        tags: [] as ProductTagType[],
        productVariants: [
            {
                id: "1",
                label: "Standard",
                price: 1200,
                oldPrice: 1500,
                displayOrder: 1
            }
        ] as ProductVariantDTO[]
    };

    const mockCategories = [
        { id: "1", name: "Predjela", slug: "predjela", order: 1 },
        { id: "2", name: "Glavna jela", slug: "glavna-jela", order: 2 },
        { id: "3", name: "Desert", slug: "desert", order: 3 },
    ];

    const mockSubcategories = [
        { id: "1", name: "Piletina", slug: "piletina", order: 1 },
        { id: "2", name: "Riba", slug: "riba", order: 2 },
        { id: "3", name: "Vegetarijanska", slug: "vegetarijanska", order: 3 },
    ];

    const mockTags = [
        ProductTagType.Protein,
        ProductTagType.Vegan,
        ProductTagType.GlutenFree
    ];


    useEffect(() => {
        fetchTheme();
    }, [localId]);


    const fetchTheme = async () => {

        if (!localId)
            return;

        setLoading(true);
        try {
            const response = await themeService.getThemeByLocalId(localId);

            if (response.data && response.success) {
                setTheme(response.data);
            } else {
                notify("error", "Greška prilikom učitavanja teme");
            }
        } catch (error: any) {
            notify("error", "Greška prilikom učitavanja teme");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    }

    const handleSaveSettings = async () => {

        if (!theme || !localId)
            return;

        setLoading(true);
        try {

            let updatedTheme: ThemeInfoDTO = {
                ...theme,
                primaryColor: theme.primaryColor,
                secondaryColor: theme.secondaryColor,
                tertiaryColor: theme.tertiaryColor
            };

            const response = await themeService.update(localId, theme.id, updatedTheme);
            if (response.success) {
                notify("success", "Tema uspešno sačuvana");
            } else {
                notify("error", "Greška prilikom čuvanja teme");
            }
        } catch (error: any) {
            notify("error", "Greška prilikom čuvanja teme");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const googleFonts = [
        "Lato",
        "Roboto",
        "Open Sans",
        "Playfair Display",
        "Poppins",
        "Montserrat",
        "Raleway",
        "Inter",
        "Ubuntu",
        "Merriweather",
        "Cormorant Garamond",
        "Bodoni Moda",
        "Cinzel",
        "Abril Fatface",
        "Bebas Neue",
        "Cardo",
        "Lora",
        "Crimson Text",
        "Dancing Script",
        "Great Vibes",
        "Sacramento",
        "Tangerine",
        "Cookie",
        "Indie Flower",
        "Sofia",
        "Pacifico",
        "Kaushan Script",
        "Dosis",
        "Fjalla One",
        "Caveat",
        "Shadows Into Light"
    ];

    const handleFontChange = (fontName: string) => {

        setTheme(prev => prev ? { ...prev, fontFamily: fontName } : prev);

        const fontLink = document.querySelector(`link[href*="family=${fontName.replace(/\s+/g, '+')}"]`);
        if (!fontLink) {
            const link = document.createElement("link");
            link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, "+")}:wght@400;700&display=swap`;
            link.rel = "stylesheet";
            document.head.appendChild(link);
        }
    };


    if (loading) {
        return <Loader />
    }


    return (
        <section className="color-section">
            <div className="w-layout-blockcontainer container color w-container">
                <div className="sttings-content-wrapper">
                    <div className="header-div">
                        <div>
                            <h2 className="heading-3">Vizuelni identitet</h2>
                            <div className="text-block">
                                Prilagodite boje i tipografiju vašeg menija<br />
                            </div>
                        </div>
                        <div className="header-button-div">
                            <div className="button-primary" onClick={() => handleSaveSettings()}>
                                <img loading="lazy" src={DisketteSaveIcon} alt="" className="button-image" />
                                <div className="button-text">Sačuvaj podešavanja</div>
                            </div>
                        </div>
                    </div>
                    <div className="color-settings-main-div">
                        <div className="color-settings-block">
                            <div className="admin-settings-box-div color-pick">
                                <div className="list-header color">
                                    <div className="list-header-info">
                                        <div className="list-header-title color">Boje</div>
                                        <div className="list-header-description">
                                            Izaberite 2-5 boja koje reprezentuju vaš brend<br />
                                        </div>
                                    </div>
                                    <div className="color-pick-list-div">
                                        <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076adc-1cee91cf" className="color-pick-div">
                                            <div className="color-pick-title">Primarna boja</div>
                                            <div className="color-pick-block">
                                                <div className="color-div-wrapper">
                                                    <div className="color-div" style={{ backgroundColor: theme?.primaryColor, cursor: "pointer" }}
                                                        onClick={() => document.getElementById("color-picker-primary")?.click()
                                                        }>
                                                    </div>
                                                    <input
                                                        id="color-picker-primary"
                                                        type="color"
                                                        value={theme?.primaryColor}
                                                        onChange={(e) => setTheme(prev => prev ? {
                                                            ...prev,
                                                            primaryColor: e.target.value
                                                        } : prev)}
                                                        style={{ display: "none" }}
                                                    />
                                                </div>
                                                <div className="form-block w-form">
                                                    <form className="form-3">
                                                        <input
                                                            className="form-input color-hex-text w-input"
                                                            placeholder="Unesite heksadecimalni zapis boje npr. #D4AF37"
                                                            type="text"
                                                            value={theme?.primaryColor}
                                                            onChange={(e) => setTheme(prev => prev ? {
                                                                ...prev,
                                                                primaryColor: e.target.value
                                                            } : prev)} />
                                                    </form>
                                                </div>
                                            </div>
                                            <div className="description-text">
                                                Koristi se za dugmad, naglašene elemente<br />
                                            </div>
                                        </div>

                                        {/*
                                         <div className="color-pick-div">
                                            <div className="color-pick-title">Sekundarna boja</div>
                                            <div className="color-pick-block">
                                                <div className="color-div-wrapper">
                                                    <div className="color-div" style={{ backgroundColor: colors.secondary, cursor: "pointer" }} onClick={() => document.getElementById("color-picker-secondary")?.click()}></div>
                                                    <input
                                                        id="color-picker-secondary"
                                                        type="color"
                                                        value={colors.secondary}
                                                        onChange={(e) => handleColorChange("secondary", e.target.value)}
                                                        style={{ display: "none" }}
                                                    />
                                                </div>
                                                <div className="form-block w-form">
                                                    <form className="form-3">
                                                        <input
                                                            className="form-input color-hex-text w-input"
                                                            placeholder="#D4AF37"
                                                            type="text"
                                                            value={colors.secondary}
                                                            onChange={(e) => handleColorChange("secondary", e.target.value)}
                                                        />
                                                    </form>
                                                </div>
                                            </div>
                                            <div className="description-text">
                                                Koristi se za podkategorije, dodatne akcente<br />
                                            </div>
                                        </div>

                                         <div className="color-pick-div">
                                            <div className="color-pick-title">Akcetna boja</div>
                                            <div className="color-pick-block">
                                                <div className="color-div-wrapper">
                                                    <div className="color-div"></div>
                                                </div>
                                                <div className="form-block w-form">
                                                    <form className="form-3">
                                                        <input className="form-input color-hex-text w-input" placeholder="#D4AF37" type="text" />
                                                    </form>
                                                </div>
                                            </div>
                                        </div> 

                                        <div className="color-pick-div">
                                            <div className="color-pick-title">Boja pozadine</div>
                                            <div className="color-pick-block">
                                                <div className="color-div-wrapper">
                                                    <div className="color-div" style={{ backgroundColor: colors.background, cursor: "pointer" }} onClick={() => document.getElementById("color-picker-background")?.click()}></div>
                                                    <input
                                                        id="color-picker-background"
                                                        type="color"
                                                        value={colors.background}
                                                        onChange={(e) => handleColorChange("background", e.target.value)}
                                                        style={{ display: "none" }}
                                                    />
                                                </div>
                                                <div className="form-block w-form">
                                                    <form className="form-3">
                                                        <input
                                                            className="form-input color-hex-text w-input"
                                                            placeholder="#D4AF37"
                                                            type="text"
                                                            value={colors.background}
                                                            onChange={(e) => handleColorChange("background", e.target.value)}
                                                        />
                                                    </form>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="color-pick-div">
                                            <div className="color-pick-title">Boja teksta</div>
                                            <div className="color-pick-block">
                                                <div className="color-div-wrapper">
                                                    <div className="color-div"></div>
                                                </div>
                                                <div className="form-block w-form">
                                                    <form className="form-3">
                                                        <input className="form-input color-hex-text w-input" placeholder="#D4AF37" type="text" />
                                                    </form>

                                                </div>
                                            </div>
                                        </div>  */}

                                    </div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div font-settings">
                                <div className="list-header-info">
                                    <div className="list-header-title font">Tipografija</div>
                                    <div className="list-header-description">
                                        Izaberite font koji odgovara vašem brendu<br />
                                    </div>
                                </div>
                                <div className="font-select-div">
                                    <div className="font-select-title">Font</div>
                                    <div className="w-form">
                                        <form className="form-2">
                                            <select
                                                className="form-select font-select w-select"
                                                value={theme?.fontFamily}
                                                onChange={(e) => handleFontChange(e.target.value)}
                                            >
                                                {googleFonts.map((font) => (
                                                    <option key={font} value={font}>
                                                        {font}
                                                    </option>
                                                ))}
                                            </select>
                                        </form>
                                    </div>
                                    <div className="font-exaple-div" style={{ fontFamily: theme?.fontFamily }}>
                                        <div className="font-example-text" style={{ fontFamily: theme?.fontFamily }}>Primer naslova</div>
                                        <div className="font-example-title" style={{ fontFamily: theme?.fontFamily }}>
                                            Ovo je primer teksta koji pokazuje kako će font izgledati u vašem meniju.<br />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div quick-pick-color-block">
                                <div className="list-header-info">
                                    <div className="list-header-title quick-pick">Brzo biranje</div>
                                    <div className="list-header-description">
                                        Izaberite gotovu kombinaciju boja<br />
                                    </div>
                                </div>
                                <div className="quick-color-pick-list">
                                    <div className="quick-color-pick-div"
                                        onClick={() => {
                                            setTheme(prev => prev ? {
                                                ...prev,
                                                primaryColor: "#D4AF37",
                                                secondaryColor: "#C9A961",
                                                tertiaryColor: "#ffffff"
                                            } : prev);
                                        }}>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#D4AF37" }}></div>
                                        {/* <div className="quick-color-pick-color-div" style={{ backgroundColor: "#C9A961" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#ffffff" }}></div> */}
                                        <div className="text-block-3">Zlatna elegancija</div>
                                    </div>
                                    <div className="quick-color-pick-div"
                                        onClick={() => {
                                            setTheme(prev => prev ? {
                                                ...prev,
                                                primaryColor: "#1E3A8A",
                                                secondaryColor: "#3B82F6",
                                                tertiaryColor: "#F8FAFC"
                                            } : prev);
                                        }}
                                    >
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#1E3A8A" }}></div>
                                        {/* <div className="quick-color-pick-color-div" style={{ backgroundColor: "#3B82F6" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#F8FAFC" }}></div> */}
                                        <div className="text-block-3">Moderna plava</div>
                                    </div>
                                    <div className="quick-color-pick-div"
                                        onClick={() => {
                                            setTheme(prev => prev ? {
                                                ...prev,
                                                primaryColor: "#16A34A",
                                                secondaryColor: "#86EFAC",
                                                tertiaryColor: "#F0FDF4"
                                            } : prev);
                                        }}
                                    >
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#16A34A" }}></div>
                                        {/* <div className="quick-color-pick-color-div" style={{ backgroundColor: "#86EFAC" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#F0FDF4" }}></div> */}
                                        <div className="text-block-3">Prirodna zelena</div>
                                    </div>
                                    {/* <div className="quick-color-pick-div" onClick={() => setColors({ primary: "#D4AF37", secondary: "#1F2937", background: "#111827" })}>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#D4AF37" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#1F2937" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#111827" }}></div>
                                        <div className="text-block-3">Luksuzno zlatno-crna</div>
                                    </div> */}
                                    <div className="quick-color-pick-div"
                                        onClick={() => {
                                            setTheme(prev => prev ? {
                                                ...prev,
                                                primaryColor: "#DC2626",
                                                secondaryColor: "#EF4444",
                                                tertiaryColor: "#FEF2F2"
                                            } : prev)
                                        }}
                                    >
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#DC2626" }}></div>
                                        {/* <div className="quick-color-pick-color-div" style={{ backgroundColor: "#EF4444" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#FEF2F2" }}></div> */}
                                        <div className="text-block-3">Energična crvena</div>
                                    </div>
                                    <div className="quick-color-pick-div"
                                        onClick={() => {
                                            setTheme(prev => prev ? {
                                                ...prev,
                                                primaryColor: "#7C3AED",
                                                secondaryColor: "#A78BFA",
                                                tertiaryColor: "#F5F3FF"
                                            } : prev)
                                        }}
                                    >
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#7C3AED" }}></div>
                                        {/* <div className="quick-color-pick-color-div" style={{ backgroundColor: "#A78BFA" }}></div>
                                        <div className="quick-color-pick-color-div" style={{ backgroundColor: "#F5F3FF" }}></div> */}
                                        <div className="text-block-3">Premijum ljubičasta</div>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div quick-pick-color-block">
                                <div className="list-header-info">
                                    <div className="list-header-title quick-pick">Izgled prikaza liste kategorija, podkategorija i proizvoda</div>
                                    <div className="list-header-description">
                                        Izaberite stil za prikaz liste kategorija, podkategorija i proizvoda<br />
                                    </div>
                                </div>

                                <div className="category-button-style-block">
                                    <div className="text-block-25">Stil za kategorije</div>
                                    <div className="category-button-style-list">

                                        <div className={"category-button-style-div " + (theme?.categoryStyle === CategoryStyleType.Style_1 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, categoryStyle: CategoryStyleType.Style_1 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_1}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.categoryStyle === CategoryStyleType.Style_2 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, categoryStyle: CategoryStyleType.Style_2 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.categoryStyle === CategoryStyleType.Style_2_1 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, categoryStyle: CategoryStyleType.Style_2_1 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2_1}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.categoryStyle === CategoryStyleType.Style_2_2 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, categoryStyle: CategoryStyleType.Style_2_2 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2_2}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.categoryStyle === CategoryStyleType.Style_3 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, categoryStyle: CategoryStyleType.Style_3 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_3}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                    </div>
                                </div>

                                <div className="category-button-style-block">
                                    <div className="text-block-25">Stil za podkategorije</div>
                                    <div className="category-button-style-list">

                                        <div className={"category-button-style-div " + (theme?.subcategoryStyle === CategoryStyleType.Style_1 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, subcategoryStyle: CategoryStyleType.Style_1 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_1}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.subcategoryStyle === CategoryStyleType.Style_2 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, subcategoryStyle: CategoryStyleType.Style_2 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.subcategoryStyle === CategoryStyleType.Style_2_1 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, subcategoryStyle: CategoryStyleType.Style_2_1 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2_1}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.subcategoryStyle === CategoryStyleType.Style_2_2 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, subcategoryStyle: CategoryStyleType.Style_2_2 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_2_2}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1"
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.subcategoryStyle === CategoryStyleType.Style_3 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, subcategoryStyle: CategoryStyleType.Style_3 } : prev)}>
                                            <CategoryTypes
                                                style={CategoryStyleType.Style_3}
                                                primaryColor={theme?.primaryColor}
                                                items={mockCategories}
                                                selectedItemId="1" />
                                        </div>

                                    </div>
                                </div>

                                <div className="category-button-style-block">
                                    <div className="text-block-25">Stil za Tagove</div>
                                    <div className="category-button-style-list">

                                        <div className={`category-button-style-div ${theme?.tagStyle === TagStyleType.Colored ? "select" : ""}`}
                                            onClick={() => setTheme(prev => prev ? { ...prev, tagStyle: TagStyleType.Colored } : prev)}
                                        >
                                            <TagTypes
                                                style={TagStyleType.Colored}
                                                primaryColor={theme?.primaryColor}
                                                tags={mockTags}
                                            />
                                        </div>

                                        <div className={`category-button-style-div ${theme?.tagStyle === TagStyleType.Basic ? "select" : ""}`}
                                            onClick={() => setTheme(prev => prev ? { ...prev, tagStyle: TagStyleType.Basic } : prev)}
                                        >
                                            <TagTypes
                                                style={TagStyleType.Basic}
                                                primaryColor={theme?.primaryColor}
                                                tags={mockTags}
                                            />
                                        </div>

                                        <div className={`category-button-style-div ${theme?.tagStyle === TagStyleType.Basic_rounded ? "select" : ""}`}
                                            onClick={() => setTheme(prev => prev ? { ...prev, tagStyle: TagStyleType.Basic_rounded } : prev)}
                                        >
                                            <TagTypes
                                                style={TagStyleType.Basic_rounded}
                                                primaryColor={theme?.primaryColor}
                                                tags={mockTags}
                                            />
                                        </div>

                                    </div>
                                </div>


                                <div className="category-button-style-block">
                                    <div className="text-block-25">Stil za Proizvode</div>
                                    <div className="category-button-style-list width-360">

                                        <div className={"category-button-style-div " + (theme?.productCardStyle === ProductCardStyleType.Style_1 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, productCardStyle: ProductCardStyleType.Style_1 } : prev)}>
                                            <ProductCardType1
                                                primaryColor={theme?.primaryColor}
                                                tagTypes={theme?.tagStyle}
                                                product={mockProduct}
                                                cart={cart}
                                                showAddToCartButton={true}
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.productCardStyle === ProductCardStyleType.Style_2 ? " select" : "")}
                                            onClick={() => setTheme(prev => prev ? { ...prev, productCardStyle: ProductCardStyleType.Style_2 } : prev)}>
                                            <ProductCardType2
                                                primaryColor={theme?.primaryColor}
                                                tagTypes={theme?.tagStyle}
                                                product={mockProduct}
                                                cart={cart}
                                                showAddToCartButton={true}
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.productCardStyle === ProductCardStyleType.Style_3 ? " select" : "")}
                                            style={{ width: "100%" }}
                                            onClick={() => setTheme(prev => prev ? { ...prev, productCardStyle: ProductCardStyleType.Style_3 } : prev)}>
                                            <ProductCardType3
                                                primaryColor={theme?.primaryColor}
                                                tagTypes={theme?.tagStyle}
                                                product={mockProduct}
                                                cart={cart}
                                                showAddToCartButton={true}
                                            />
                                        </div>

                                        <div className={"category-button-style-div " + (theme?.productCardStyle === ProductCardStyleType.Style_4 ? " select" : "")}
                                            style={{ width: "100%", marginTop: "1rem" }}
                                            onClick={() => setTheme(prev => prev ? { ...prev, productCardStyle: ProductCardStyleType.Style_4 } : prev)}>
                                            <ProductCardType4
                                                primaryColor={theme?.primaryColor}
                                                tagTypes={theme?.tagStyle}
                                                product={mockProduct}
                                                cart={cart}
                                                showAddToCartButton={true}
                                            />
                                        </div>

                                    </div>
                                </div>

                            </div>

                        </div>

                        <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076b60-1cee91cf" className="admin-settings-box-div style-preview-block">
                            <div className="list-header-info">
                                <div className="list-header-title preview">Pregled</div>
                                <div className="list-header-description">
                                    Kako će vaš meni izgledati sa odabranim bojama<br />
                                </div>
                            </div>
                            <div className="style-preview-div" style={{ fontFamily: theme?.fontFamily }}>
                                <div className="preview-header" style={{ backgroundColor: theme?.primaryColor }}>
                                    <div className="preview-image-div"></div>
                                    <div className="preview-local-info">
                                        <div className="preview-local-wifi">Restoran Zlatna Lađa</div>
                                        <div className="preview-local-name">WiFi: lozinka123</div>
                                    </div>
                                </div>

                                <CategoryTypes
                                    style={theme?.categoryStyle}
                                    primaryColor={theme?.primaryColor}
                                    secondaryColor={theme?.secondaryColor}
                                    items={mockCategories}
                                    selectedItemId="1"
                                />

                                <CategoryTypes
                                    style={theme?.subcategoryStyle}
                                    primaryColor={theme?.primaryColor}
                                    secondaryColor={theme?.secondaryColor}
                                    items={mockSubcategories}
                                    selectedItemId="1"
                                />

                                <div className="preview-produst-list">
                                    {Array.from({ length: 2 }).map((_, index) => {
                                        switch (theme?.productCardStyle) {
                                            case ProductCardStyleType.Style_1:
                                                return (
                                                    <ProductCardType1
                                                        key={index}
                                                        primaryColor={theme?.primaryColor}
                                                        tagTypes={theme?.tagStyle}
                                                        product={mockProduct}
                                                        cart={cart}
                                                        showAddToCartButton={true}
                                                    />
                                                );

                                            case ProductCardStyleType.Style_3:
                                                return (
                                                    <ProductCardType3
                                                        key={index}
                                                        primaryColor={theme?.primaryColor}
                                                        tagTypes={theme?.tagStyle}
                                                        product={mockProduct}
                                                        cart={cart}
                                                        showAddToCartButton={true}
                                                    />
                                                );

                                            case ProductCardStyleType.Style_4:
                                                return (
                                                    <ProductCardType4
                                                        key={index}
                                                        primaryColor={theme?.primaryColor}
                                                        tagTypes={theme?.tagStyle}
                                                        product={mockProduct}
                                                        cart={cart}
                                                        showAddToCartButton={true}
                                                    />
                                                );

                                            case ProductCardStyleType.Style_2:
                                            default:
                                                return (
                                                    <ProductCardType2
                                                        key={index}
                                                        primaryColor={theme?.primaryColor}
                                                        tagTypes={theme?.tagStyle}
                                                        product={mockProduct}
                                                        cart={cart}
                                                        showAddToCartButton={true}
                                                    />
                                                );
                                        }
                                    })}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section >
    );
};

export default ThemePage;